import React, { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getPropertyDetail } from "reduxStore/action/propertyActions";
import {
  getConfiguration,
  getMasterList,
} from "reduxStore/action/masterActions";
import strings from "translate/language";
import {
  handleDetailResponse,
  handleListResponse,
  seo,
} from "utils/utils";
import { MasterTypeHelper } from "utils/constants";
import ImageRender from "component/common/ImageRender";
import moment from "moment";
import VideoModel from "../../component/common/videoModel";
import Breadcrumb from "component/common/breadcrumb";
import PdfViewer from "component/common/pdfViewer";
import "./PropertyListing.css";

const displayValue = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return strings.notavailable;
  }
  return value;
};

const FactCell = ({ label, value, capitalize = false }) => (
  <div className="justo-property-detail__fact">
    <span className="justo-property-detail__fact-label">{label}</span>
    <span
      className={`justo-property-detail__fact-value${
        capitalize ? " justo-property-detail__fact-value--cap" : ""
      }`}
    >
      {value}
    </span>
  </div>
);

const FactSection = ({ title, children, className = "" }) => (
  <div className={`justo-property-detail__section ${className}`.trim()}>
    <h2 className="justo-property-detail__section-title">{title}</h2>
    <div className="justo-property-detail__facts">{children}</div>
  </div>
);

const ALLOCATION_CHIP_PREVIEW = 16;

const AllocationGroup = ({ roleTitle, users }) => {
  const [expanded, setExpanded] = useState(false);
  const names = (users || [])
    .map((u) => u?.user_name)
    .filter((name) => name != null && String(name).trim() !== "");
  const needsToggle = names.length > ALLOCATION_CHIP_PREVIEW;
  const visible =
    expanded || !needsToggle
      ? names
      : names.slice(0, ALLOCATION_CHIP_PREVIEW);

  return (
    <div className="justo-property-detail__alloc-group">
      <div className="justo-property-detail__alloc-head">
        <span className="justo-property-detail__alloc-role">
          {roleTitle || strings.role}
        </span>
        <span className="justo-property-detail__alloc-count">
          {names.length}
        </span>
      </div>
      {names.length === 0 ? (
        <p className="justo-property-detail__alloc-empty">
          {strings.notavailable}
        </p>
      ) : (
        <>
          <div
            className={`justo-property-detail__alloc-chips${
              expanded ? " justo-property-detail__alloc-chips--open" : ""
            }`}
          >
            {visible.map((name, index) => (
              <span key={`${name}-${index}`} className="justo-property-detail__chip">
                {name}
              </span>
            ))}
          </div>
          {needsToggle ? (
            <button
              type="button"
              className="justo-property-detail__alloc-toggle"
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded
                ? "Show less"
                : `Show all ${names.length}`}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
};

const handleStringObj = (data) => {
  let obj = {};
  if (data && data !== "[object Object]") {
    const typeofAdr = typeof data;
    if (typeofAdr === "string" || typeofAdr === "object") {
      try {
        obj = typeofAdr === "object" ? data : JSON.parse(data);
      } catch (e) {
        obj = {};
      }
    }
  }
  return obj;
};

const formatDateTime = (value) =>
  value ? moment(value).format("DD-MM-yyyy hh:mm a") : strings.notavailable;

const formatDate = (value) =>
  value ? moment(value).format("DD-MM-yyyy") : strings.notavailable;

const yesNo = (value) => (value ? "Yes" : "No");

const ViewProperty = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { property_id = "" } = params || {};
  const dispatch = useDispatch();

  const [viewDetails, setViewDetails] = useState({});
  const [getbusinessDM, setGetBusinessDM] = useState({});
  const [getmarketingAI, setGetMarketingAI] = useState({});
  const [getMarketingBD, setGetMarketingBD] = useState({});
  const [unitTypeData, setUnitTypeData] = useState([]);
  const [typologyData, setTypologyData] = useState([]);
  const [videoDetails, setVideoDetails] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isPdfView, setIsPdfView] = useState(false);

  const getPropertyDetailSelector =
    useSelector((state) => state.propertyData) || [];
  const unitTypeSelector = useSelector((state) => state.masterData) || {};
  const configurationsSelector =
    useSelector((state) => state.configurations) || {};

  const navigatpage = (navname) => navigate(navname);

  const unitTypeTitle = (unitTypeId) =>
    handleDetailResponse(unitTypeData.filter((i) => i._id === unitTypeId))
      ?.title || "";

  const unitWithType = (field) => {
    if (!field?.units && field?.units !== 0) return strings.notavailable;
    const type = unitTypeTitle(field?.unitType);
    return type ? `${field.units} ${type}` : String(field.units);
  };

  useEffect(() => {
    if (getPropertyDetailSelector.response) {
      const { response } = getPropertyDetailSelector;
      if (response?.status === 200) {
        const data = handleDetailResponse(response?.data);
        setViewDetails(data);
        setGetBusinessDM(
          handleDetailResponse(
            handleStringObj(data?.business_development_marketing)
          )
        );
        setGetMarketingAI(
          handleDetailResponse(
            handleStringObj(data?.marketing_assistance_information)
          )
        );
        setGetMarketingBD(
          handleDetailResponse(
            handleStringObj(data?.marketing_business_development)
          )
        );
      } else {
        setViewDetails({});
        setGetBusinessDM({});
        setGetMarketingAI({});
        setGetMarketingBD({});
      }
    }
  }, [getPropertyDetailSelector]);

  useEffect(() => {
    if (unitTypeSelector.response) {
      const { response } = unitTypeSelector;
      if (response?.status === 200) {
        setUnitTypeData(handleListResponse(response.data));
      } else {
        setUnitTypeData([]);
      }
    }
  }, [unitTypeSelector]);

  useEffect(() => {
    if (configurationsSelector.response) {
      const { response } = configurationsSelector;
      if (response?.status === 200) {
        setTypologyData(handleListResponse(response.data));
      } else {
        setTypologyData([]);
      }
    }
  }, [configurationsSelector]);

  useLayoutEffect(() => {
    dispatch(getPropertyDetail({ property_id }));
    dispatch(getConfiguration());
    dispatch(getMasterList({ type: MasterTypeHelper.unitType }));
  }, [dispatch, property_id]);

  useEffect(() => {
    seo({
      title: "Property Details",
      metaDescription: "View property details",
      canonicalLink: window.location.href,
    });
  }, []);

  const isActive = Boolean(viewDetails?.status);
  const propertyType =
    viewDetails?.property_type_data && viewDetails?.property_type_title
      ? viewDetails.property_type_title
      : strings.notavailable;

  const amenities =
    viewDetails?.amenity && viewDetails.amenity.length > 0
      ? viewDetails.amenity.map((item) => item?.title).join(", ")
      : strings.notavailable;

  const typologyTitle =
    handleDetailResponse(
      typologyData.filter((i) => i._id === getbusinessDM?.typology)
    )?.title || strings.notavailable;

  const docs = viewDetails?.property_document || [];
  const images = docs.filter((item) => item.document_type === "image");
  const videos = docs.filter((item) => item.document_type === "video");
  const catalogues = docs.filter((item) => item.document_type === "document");

  if (isPdfView) {
    return (
      <PdfViewer
        pdfUrl={pdfUrl}
        backButton={() => {
          setIsPdfView(false);
          setPdfUrl(null);
        }}
      />
    );
  }

  return (
    <div className="page-body justo-property-page">
      <VideoModel data={videoDetails} />
      <Breadcrumb
        title={strings.propertyDetails}
        url={"/property"}
        parentListing={strings.propertyListing}
        titleactive={strings.viewPropertyDetails}
        isBackBtn={true}
      />
      <div className="container-fluid justo-property">
        <div className="justo-property__card justo-property-detail">
          <div className="justo-property-detail__hero">
            <div className="justo-property-detail__hero-main">
              <h1 className="justo-property-detail__title">
                {displayValue(viewDetails?.property_title)}
              </h1>
              <p className="justo-property-detail__meta">
                <span>{displayValue(viewDetails?.city)}</span>
                <span>{displayValue(viewDetails?.location)}</span>
                <span>{propertyType}</span>
              </p>
            </div>
            <div className="justo-property-detail__hero-actions">
              <span
                className={`property-status-badge property-status-badge--detail ${
                  isActive
                    ? "property-status-badge--active"
                    : "property-status-badge--inactive"
                }`}
              >
                <i
                  className={`fa ${
                    isActive ? "fa-check-circle" : "fa-exclamation-circle"
                  } property-status-badge__icon`}
                  aria-hidden="true"
                />
                <span className="property-status-badge__label">
                  {isActive ? strings.active : strings.deactive}
                </span>
              </span>
            </div>
          </div>

          <div className="justo-property-detail__stats">
            <div className="justo-property-detail__stat">
              <span className="justo-property-detail__stat-label">
                {strings.totalFlat}
              </span>
              <span className="justo-property-detail__stat-value">
                {viewDetails?.total_invantry || 0}
              </span>
            </div>
            <div className="justo-property-detail__stat">
              <span className="justo-property-detail__stat-label">
                {strings.soldOut}
              </span>
              <span className="justo-property-detail__stat-value">
                {viewDetails?.sold_out || 0}
              </span>
            </div>
            <div className="justo-property-detail__stat">
              <span className="justo-property-detail__stat-label">
                {strings.remaining}
              </span>
              <span className="justo-property-detail__stat-value">
                {displayValue(viewDetails?.total_remaining)}
              </span>
            </div>
            <div className="justo-property-detail__stat">
              <span className="justo-property-detail__stat-label">
                {strings.noOfVisitor}
              </span>
              <span className="justo-property-detail__stat-value">
                {viewDetails?.total_visitor || 0}
              </span>
            </div>
            <div className="justo-property-detail__stat">
              <span className="justo-property-detail__stat-label">
                {strings.noOfSiteVisit}
              </span>
              <span className="justo-property-detail__stat-value">
                {viewDetails?.site_visit || 0}
              </span>
            </div>
            <div className="justo-property-detail__stat">
              <span className="justo-property-detail__stat-label">
                {strings.closeVisit}
              </span>
              <span className="justo-property-detail__stat-value">
                {viewDetails?.close_visit || 0}
              </span>
            </div>
          </div>

          <div className="justo-property-detail__content">
            <div className="justo-property-detail__grid justo-property-detail__grid--2">
              <FactSection title="Overview">
                <FactCell label={strings.type} value={propertyType} />
                <FactCell
                  label={strings.city}
                  value={displayValue(viewDetails?.city)}
                />
                <FactCell
                  label={strings.locality}
                  value={displayValue(viewDetails?.location)}
                />
                <FactCell
                  label={strings.locationArea}
                  value={displayValue(viewDetails?.area)}
                />
                <FactCell
                  label={strings.pickupAllowed}
                  value={displayValue(viewDetails?.pickup)}
                />
                <FactCell label={strings.amenity} value={amenities} />
              </FactSection>

              <FactSection title="CRM & Dates">
                <FactCell
                  label={strings.crmPersonEmail}
                  value={displayValue(viewDetails?.crm_person_email)}
                />
                <FactCell
                  label={strings.jqProjectID}
                  value={displayValue(viewDetails?.jw_project_id)}
                />
                <FactCell
                  label={strings.createdDate}
                  value={formatDateTime(viewDetails?.createdDate)}
                />
                <FactCell
                  label={strings.startDate}
                  value={formatDate(viewDetails?.start_date)}
                />
                <FactCell
                  label={strings.endDate}
                  value={formatDate(viewDetails?.end_date)}
                />
              </FactSection>
            </div>

            <div className="justo-property-detail__grid justo-property-detail__grid--wide">
              <div className="justo-property-detail__section">
                <h2 className="justo-property-detail__section-title">
                  {strings.allocated}
                </h2>
                <div className="justo-property-detail__alloc">
                  {viewDetails?.property_allocateds?.length > 0 ? (
                    viewDetails.property_allocateds.map((item, index) => (
                      <AllocationGroup
                        key={index}
                        roleTitle={item?.role_title}
                        users={item?.users}
                      />
                    ))
                  ) : (
                    <p className="justo-property-detail__alloc-empty">
                      {strings.notavailable}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="justo-property-detail__grid justo-property-detail__grid--wide">
              <div className="justo-property-detail__section">
                <h2 className="justo-property-detail__section-title">
                  {strings.configuration}
                </h2>
                <div className="justo-property-detail__config-grid">
                  {viewDetails?.configuration_area?.length > 0 ? (
                    viewDetails.configuration_area.map((item, index) => (
                      <div
                        key={index}
                        className="justo-property-detail__config-card"
                      >
                        <strong>{item.configuration_title}</strong>
                        <span>
                          Carpet {item.area} sq.ft · Min {item.min_rate}
                          {item.min_rate_type} · Max {item.max_rate}
                          {item.max_rate_type}
                        </span>
                        <span className="justo-property-detail__config-meta">
                          Inventory {item.inventry} · Sold {item.sold_out} ·
                          Remaining {item.remaining}
                        </span>
                      </div>
                    ))
                  ) : (
                    <p className="justo-property-detail__alloc-empty">
                      {strings.notavailable}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="justo-property-detail__grid justo-property-detail__grid--wide">
              <FactSection title={strings.content}>
                {docs.length === 0 ? (
                  <FactCell
                    label={strings.content}
                    value={strings.notavailable}
                  />
                ) : (
                  <div className="justo-property-detail__media">
                    {images.length > 0 && (
                      <div className="justo-property-detail__media-block">
                        <h3 className="justo-property-detail__media-title">
                          {strings.images}
                        </h3>
                        <div className="justo-property-detail__media-row">
                          {images.slice(0, 3).map((item, index) => (
                            <ImageRender
                              key={index}
                              baseUrl={viewDetails?.base_url}
                              imageUrl={item.document}
                              thumbnailUrl={item.document}
                            />
                          ))}
                          {images.length > 3 && (
                            <button
                              type="button"
                              className="justo-property-detail__see-more"
                              onClick={() =>
                                navigatpage(
                                  `/viewpropertynext/image/${property_id}`
                                )
                              }
                              aria-label="See more images"
                            >
                              <i
                                className="fa fa-arrow-circle-o-right"
                                aria-hidden="true"
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    {videos.length > 0 && (
                      <div className="justo-property-detail__media-block">
                        <h3 className="justo-property-detail__media-title">
                          {strings.videos}
                        </h3>
                        <div className="justo-property-detail__media-row">
                          {videos.slice(0, 3).map((item, i) => (
                            <div
                              key={i}
                              className="justo-property-detail__video-thumb"
                            >
                              <img
                                src={
                                  viewDetails.base_url + item.video_thumbnail
                                }
                                alt=""
                              />
                              <button
                                type="button"
                                className="justo-property-detail__play"
                                data-toggle="modal"
                                data-target="#exampleModal"
                                onClick={() => {
                                  setVideoDetails({
                                    ...item,
                                    base_url: viewDetails.base_url,
                                  });
                                }}
                                aria-label="Play video"
                              >
                                <i className="fa fa-play" aria-hidden="true" />
                              </button>
                            </div>
                          ))}
                          {videos.length > 3 && (
                            <button
                              type="button"
                              className="justo-property-detail__see-more"
                              onClick={() =>
                                navigatpage(
                                  `/viewpropertynext/video/${property_id}`
                                )
                              }
                              aria-label="See more videos"
                            >
                              <i
                                className="fa fa-arrow-circle-o-right"
                                aria-hidden="true"
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    {catalogues.length > 0 && (
                      <div className="justo-property-detail__media-block">
                        <h3 className="justo-property-detail__media-title">
                          {strings.catalogue}
                        </h3>
                        <div className="justo-property-detail__media-row">
                          {catalogues.slice(0, 3).map((item, index) => (
                            <button
                              key={index}
                              type="button"
                              className="justo-property-detail__doc"
                              onClick={() => {
                                setPdfUrl(
                                  viewDetails.base_url + item.document
                                );
                                setIsPdfView(true);
                              }}
                            >
                              <img
                                src="/assets/images/documentIcon.png"
                                alt={item.document || "document"}
                              />
                            </button>
                          ))}
                          {catalogues.length > 3 && (
                            <button
                              type="button"
                              className="justo-property-detail__see-more"
                              onClick={() =>
                                navigatpage(
                                  `/viewpropertynext/document/${property_id}`
                                )
                              }
                              aria-label="See more documents"
                            >
                              <i
                                className="fa fa-arrow-circle-o-right"
                                aria-hidden="true"
                              />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </FactSection>
            </div>

            <div className="justo-property-detail__grid">
              <FactSection title="Possession & Land">
                <FactCell
                  label={strings.reraPossession}
                  value={formatDateTime(getbusinessDM?.rera_possession)}
                />
                <FactCell
                  label={strings.expectedPossession}
                  value={formatDateTime(getbusinessDM?.expected_possession)}
                />
                <FactCell
                  label={strings.landparcel}
                  value={unitWithType(getbusinessDM?.land_parcel)}
                />
                <FactCell
                  label={strings.towers}
                  value={displayValue(getbusinessDM?.towers)}
                />
                <FactCell label={strings.typology} value={typologyTitle} />
                <FactCell
                  label={strings.reraCarpet}
                  value={unitWithType(getbusinessDM?.rera_carpet)}
                />
                <FactCell
                  label={strings.builtUpArea}
                  value={unitWithType(getbusinessDM?.builtup_area)}
                />
                <FactCell
                  label={strings.salabelArea}
                  value={unitWithType(getbusinessDM?.saleble_area)}
                />
              </FactSection>

              <FactSection title="Pricing & Scheme">
                <FactCell
                  label={strings.allInclusive}
                  value={displayValue(getbusinessDM?.all_inclusive)}
                />
                <FactCell
                  label={strings.paymentScheme}
                  value={displayValue(getbusinessDM?.payment_scheme)}
                />
                <FactCell
                  label={strings.tentativeClosingPrice}
                  value={displayValue(getbusinessDM?.tentative_closing_price)}
                />
                <FactCell
                  label={strings.currentStage}
                  value={displayValue(getbusinessDM?.current_stage)}
                />
                <FactCell
                  label={strings.Positive}
                  value={displayValue(getbusinessDM?.positive)}
                />
                <FactCell
                  label={strings.Negative}
                  value={displayValue(getbusinessDM?.negative)}
                />
                <FactCell
                  label={strings.supportsForCP}
                  value={displayValue(getbusinessDM?.supports_for_cp)}
                />
                <FactCell
                  label={strings.upcomingInfrastructure}
                  value={displayValue(getbusinessDM?.upcoming_infrastructure)}
                />
              </FactSection>

              <FactSection title="Digital Marketing">
                <FactCell
                  label={strings.google}
                  value={yesNo(
                    getmarketingAI?.digital_marketing?.digital_marketing_google
                  )}
                />
                <FactCell
                  label={strings.facebook}
                  value={yesNo(
                    getmarketingAI?.digital_marketing
                      ?.digital_marketing_facebook
                  )}
                />
                <FactCell
                  label={strings.instagram}
                  value={yesNo(
                    getmarketingAI?.digital_marketing
                      ?.digital_marketing_instagram
                  )}
                />
                <FactCell
                  label={strings.taboola}
                  value={yesNo(
                    getmarketingAI?.digital_marketing?.digital_marketing_taboola
                  )}
                />
                <FactCell
                  label={strings.timesquare}
                  value={yesNo(
                    getmarketingAI?.digital_marketing
                      ?.digital_marketing_timeSquare
                  )}
                />
              </FactSection>
            </div>

            <div className="justo-property-detail__grid justo-property-detail__grid--2">
              <FactSection title="Marketing Campaigns">
                <FactCell
                  label={strings.hoardings}
                  value={`${displayValue(getmarketingAI?.hoardings?.text)} · ${displayValue(getmarketingAI?.hoardings?.start_date)} → ${displayValue(getmarketingAI?.hoardings?.end_date)}`}
                />
                <FactCell
                  label={strings.newspaper}
                  value={`${displayValue(getmarketingAI?.newspaper?.text)} · ${displayValue(getmarketingAI?.newspaper?.start_date)} → ${displayValue(getmarketingAI?.newspaper?.end_date)}`}
                />
                <FactCell
                  label={strings.whatsappSMS}
                  value={`${displayValue(getmarketingAI?.whatsapp_sms?.number)} · ${displayValue(getmarketingAI?.whatsapp_sms?.start_date)} → ${displayValue(getmarketingAI?.whatsapp_sms?.end_date)}`}
                />
                <FactCell
                  label={strings.blastRadioJingles}
                  value={`${displayValue(getmarketingAI?.blast_radio_jingles?.text)} · ${displayValue(getmarketingAI?.blast_radio_jingles?.start_date)} → ${displayValue(getmarketingAI?.blast_radio_jingles?.end_date)}`}
                />
              </FactSection>

              <FactSection title="Customer Profile">
                <FactCell
                  label={strings.residentLocation}
                  value={displayValue(getMarketingBD?.resident_location)}
                />
                <FactCell
                  label={strings.officeLocation}
                  value={displayValue(getMarketingBD?.office_location)}
                />
                <FactCell
                  label={strings.occupation}
                  value={displayValue(getMarketingBD?.occupation)}
                />
                <FactCell
                  label={strings.customerDesignation}
                  value={displayValue(getMarketingBD?.customer_designation)}
                />
                <FactCell
                  label={strings.AnnualIncome}
                  value={displayValue(getMarketingBD?.annual_income)}
                />
                <FactCell
                  label={strings.ethnicity}
                  value={displayValue(getMarketingBD?.ethnicity)}
                />
                <FactCell
                  label={strings.ageGroup}
                  value={displayValue(getMarketingBD?.age_group)}
                />
                <FactCell
                  label={strings.RentedOwned}
                  value={displayValue(getMarketingBD?.rented_owned)}
                />
                <FactCell
                  label={strings.CurrentStayConfiguration}
                  value={displayValue(
                    getMarketingBD?.current_stay_configuration
                  )}
                />
                <FactCell
                  label={strings.CustomerBudget}
                  value={displayValue(getMarketingBD?.customer_budget)}
                />
                <FactCell
                  label={strings.PreferredTypology}
                  value={displayValue(getMarketingBD?.preferred_typology)}
                />
                <FactCell
                  label={strings.PurposeofPurchase}
                  value={displayValue(getMarketingBD?.purpose_of_purchase)}
                />
                <FactCell
                  label={strings.possessionexpected}
                  value={formatDate(getMarketingBD?.possessionexpected)}
                />
              </FactSection>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewProperty;
